import type {
    OperationAmountSource,
    OperationCatalog,
    OperationCatalogRow,
    OperationCategory,
    OperationLine,
    OperationLineDraft,
    OperationActivityDetail,
    OperationActivityLine,
    OperationActivityPage,
    OperationActivityRow,
    OperationActivitySlot,
    OperationAuditAction,
    OperationAuditEvent,
    OperationHistoryVersion,
    OperationLineSide,
    OperationPairRole,
    OperationPostingLine,
    OperationPreview,
    OperationReversal,
    OperationStatus,
    OperationTemplateHistory,
    OperationVersionTimelineStatus,
    OperationTemplateDetail,
    OperationText,
    OperationVersionDetail,
    OperationVersionSummary,
    RecordableOperation,
    RecordableOperations,
    RecordablePinnedLine,
    RecordableSlot,
    RecordingPeriodStatus,
    RecordedOperation,
    RecordOperationInput,
    UnrecordableReason,
    UserRole,
} from '@/api/models';

/**
 * Wire ⇄ domain mapping for the operation catalog (bos-api).
 *
 * **This is the only file that knows the server's field names.** Everything
 * downstream — the service, the query hook, the page hook, the table — sees the
 * domain shapes in `@/api/models/Operations`. Same seam, and the same reason, as
 * `accountsMapping`, `journalMapping` and `trialBalanceMapping`.
 *
 * The wire and the domain agree on nearly every name here — the response was
 * specified in `CONTRACT.md` before either side existed, so there is no
 * inherited guess to reconcile. What this file is actually for is the one
 * structural change it makes, plus the two properties a well-meaning consumer
 * would otherwise "improve":
 *
 * 1. **The union is narrowed here, once.** The wire sends one flat object per
 *    row with the inapplicable half null (`status: null` on a system row,
 *    `recipe: []` on a template), which is a sensible wire shape — a consumer
 *    reading a null beats a consumer crashing on a missing property. It is a bad
 *    *client* shape: a screen that decides "is this editable?" from a nullable
 *    field will eventually get it wrong on the row where it matters. So the
 *    discriminant is `kind` and nothing else, and the two variants carry only
 *    the fields that apply to them.
 * 2. **An unreadable row is dropped, not fatal.** The opposite of
 *    `trialBalanceMapping`, deliberately. There, a dropped row makes the printed
 *    totals disagree with the rows above them — a report that is wrong in exactly
 *    the way the screen exists to detect. Here there are no totals and no
 *    arithmetic: a catalog missing one operation is a catalog missing one
 *    operation, and it is plainly better than a Templates tab that will not open
 *    because a future server added a row shape this build has not met yet.
 * 3. **`lastUsedAt` is passed through as `null`, never defaulted.** A template
 *    that has posted nothing answers `null`, and "not known yet" must not become
 *    "never used" at the mapping boundary — the client cannot tell those apart
 *    afterwards.
 *
 * The recording half at the bottom of this file follows the same rules with one
 * exception, stated there: a posting line that cannot be read is **fatal**, not
 * dropped. It is money about to be booked.
 */

// ── wire shapes ─────────────────────────────────────────────────────────────

export type OperationTextWire = {
    ka?: string | null;
    en?: string | null;
};

export type OperationVersionSummaryWire = {
    id?: string;
    version?: number;
    effectiveFrom?: string;
    effectiveTo?: string | null;
    status?: string;
    pairCount?: number;
};

export type OperationCategoryWire = {
    id?: string;
    nameKa?: string | null;
    nameEn?: string | null;
};

export type OperationCatalogRowWire = {
    kind?: string;
    key?: string;
    nameKa?: string;
    nameEn?: string;
    category?: OperationCategoryWire | null;
    templateId?: string | null;
    status?: string | null;
    rolesAllowed?: string[] | null;
    activeVersion?: OperationVersionSummaryWire | null;
    versionCount?: number | null;
    lastUsedAt?: string | null;
    recordCount?: number | null;
    source?: string | null;
    recipe?: OperationTextWire[] | null;
};

export type OperationCatalogWire = {
    items?: OperationCatalogRowWire[] | null;
};

export type OperationCategoriesWire = {
    items?: OperationCategoryWire[] | null;
};

/**
 * `null` when the category cannot be read — no id, or no name in either
 * language.
 *
 * **Dropped, never fatal to its row.** A category is a label; an operation
 * without a chip is still an operation you can record, and losing the row would
 * cost the user the thing they came for. Both names are optional individually
 * because the set is the tenant's to write and filling one language is a normal
 * state — but a category with neither has nothing to render.
 */
export const mapOperationCategory = (
    wire: OperationCategoryWire | null | undefined,
): OperationCategory | null => {
    if (!wire?.id) {
        return null;
    }
    const nameKa = wire.nameKa ?? '';
    const nameEn = wire.nameEn ?? '';
    if (!nameKa && !nameEn) {
        return null;
    }
    return { id: wire.id, nameKa, nameEn };
};

/** `null` only when the body is not a category list at all. */
export const mapOperationCategories = (
    wire: OperationCategoriesWire,
): OperationCategory[] | null => {
    if (!Array.isArray(wire.items)) {
        return null;
    }
    return wire.items
        .map(mapOperationCategory)
        .filter((item): item is OperationCategory => item !== null);
};

// ── primitives ──────────────────────────────────────────────────────────────

const OPERATION_STATUSES: readonly string[] = ['DRAFT', 'ACTIVE', 'BLOCKED'];

const statusOf = (wire: string | null | undefined): OperationStatus | null =>
    typeof wire === 'string' && OPERATION_STATUSES.includes(wire)
        ? (wire as OperationStatus)
        : null;

const USER_ROLES: readonly string[] = ['OrgAdmin', 'BranchAdmin', 'Seller'];

/**
 * Roles this client recognises. An unknown role is dropped rather than carried
 * as a string the UI cannot label — a chip reading `SUPERVISOR` in a list of
 * translated ones is worse than a chip that is not there, and the fact it
 * conveys is one the client could not act on anyway.
 */
const rolesOf = (wire: string[] | null | undefined): UserRole[] =>
    (wire ?? []).filter((role): role is UserRole => USER_ROLES.includes(role));

const isFiniteInt = (value: unknown): value is number =>
    typeof value === 'number' && Number.isFinite(value);

const textOf = (wire: OperationTextWire): OperationText | null =>
    typeof wire.ka === 'string' && typeof wire.en === 'string'
        ? { ka: wire.ka, en: wire.en }
        : null;

// ── the active version ──────────────────────────────────────────────────────

/**
 * `null` when the template has no version in force — which the server also says
 * with a literal `null`, and which is a real state rather than an error — or
 * when the version it sent cannot be read.
 */
export const mapOperationVersion = (
    wire: OperationVersionSummaryWire | null | undefined,
): OperationVersionSummary | null => {
    if (!wire) {
        return null;
    }
    const status = statusOf(wire.status);
    if (
        !wire.id ||
        !wire.effectiveFrom ||
        !status ||
        !isFiniteInt(wire.version) ||
        !isFiniteInt(wire.pairCount)
    ) {
        return null;
    }
    return {
        id: wire.id,
        version: Math.trunc(wire.version),
        effectiveFrom: wire.effectiveFrom,
        effectiveTo: wire.effectiveTo ?? null,
        status,
        pairCount: Math.trunc(wire.pairCount),
    };
};

// ── rows ────────────────────────────────────────────────────────────────────

/**
 * `null` when the row cannot be read as either kind of operation — an unknown
 * `kind`, a missing key, a system row with no `source`, or a template with no
 * status. See the file header for why an unreadable row is dropped rather than
 * failing the whole read.
 */
export const mapOperationRow = (
    wire: OperationCatalogRowWire,
): OperationCatalogRow | null => {
    if (!wire.key || !wire.nameKa || !wire.nameEn) {
        return null;
    }
    const base = {
        key: wire.key,
        nameKa: wire.nameKa,
        nameEn: wire.nameEn,
        lastUsedAt: wire.lastUsedAt ?? null,
        // Absent or unreadable reads as zero, not as a dropped row: a missing
        // count costs the row its rank, which the server has already applied
        // anyway, and losing the row entirely would cost the catalog an
        // operation.
        recordCount:
            typeof wire.recordCount === 'number' && wire.recordCount > 0
                ? Math.trunc(wire.recordCount)
                : 0,
    };

    if (wire.kind === 'SYSTEM') {
        // A system row with no recipe would render as a read-only row that
        // explains nothing, which is the entire reason it is in the catalog.
        const recipe = (wire.recipe ?? [])
            .map(textOf)
            .filter((step): step is OperationText => step !== null);
        if (!wire.source || recipe.length === 0) {
            return null;
        }
        return { ...base, kind: 'SYSTEM', source: wire.source, recipe };
    }

    if (wire.kind === 'TEMPLATE') {
        const status = statusOf(wire.status);
        if (!wire.templateId || !status) {
            return null;
        }
        return {
            ...base,
            kind: 'TEMPLATE',
            templateId: wire.templateId,
            category: mapOperationCategory(wire.category),
            status,
            rolesAllowed: rolesOf(wire.rolesAllowed),
            activeVersion: mapOperationVersion(wire.activeVersion),
            versionCount: isFiniteInt(wire.versionCount)
                ? Math.trunc(wire.versionCount)
                : 0,
        };
    }

    return null;
};

// ── the catalog ─────────────────────────────────────────────────────────────

/**
 * `null` only when the body is not a catalog at all — a missing `items` array.
 * An empty array is a valid answer (a tenant whose chart has not been seeded
 * yet), and one the page renders as an empty catalog rather than as a failure.
 */
// ── the editor ──────────────────────────────────────────────────────────────

export type OperationLineWire = {
    id?: string;
    pairGroup?: number;
    side?: string;
    accountId?: string | null;
    slotLabelKa?: string | null;
    slotLabelEn?: string | null;
    accountFilter?: string[] | null;
    amountSource?: string;
    roleLabel?: string | null;
    pairRole?: string | null;
};

export type OperationVersionDetailWire = OperationVersionSummaryWire & {
    lines?: OperationLineWire[] | null;
};

export type OperationTemplateDetailWire = {
    key?: string;
    nameKa?: string;
    nameEn?: string;
    templateId?: string;
    categoryId?: string | null;
    status?: string;
    rolesAllowed?: string[] | null;
    versions?: OperationVersionSummaryWire[] | null;
    activeVersion?: OperationVersionDetailWire | null;
    draftVersion?: OperationVersionDetailWire | null;
};

const LINE_SIDES: readonly string[] = ['DEBIT', 'CREDIT'];

const sideOf = (wire: string | undefined): OperationLineSide | null =>
    typeof wire === 'string' && LINE_SIDES.includes(wire)
        ? (wire as OperationLineSide)
        : null;

const AMOUNT_SOURCES: readonly string[] = [
    'GROSS',
    'NET',
    'VAT_OF_GROSS',
    'USER_INPUT',
];

const amountSourceOf = (
    wire: string | undefined,
): OperationAmountSource | null =>
    typeof wire === 'string' && AMOUNT_SOURCES.includes(wire)
        ? (wire as OperationAmountSource)
        : null;

const PAIR_ROLES: readonly string[] = [
    'COGS',
    'REVENUE',
    'VAT',
    'SETTLEMENT',
    'DISCOUNT',
    'ROUNDING',
    'OTHER',
];

/**
 * An unreadable pair role degrades to `OTHER`; it does **not** fail the read.
 *
 * The opposite of how `side` and `amountSource` are treated one line up, and
 * deliberately: those decide what the recipe posts, so guessing at them would
 * change the books. A role is a label on a chip. A server that grows an eighth
 * role should leave an older client showing "Other", not showing an error page.
 */
const pairRoleOf = (wire: string | null | undefined): OperationPairRole =>
    typeof wire === 'string' && PAIR_ROLES.includes(wire)
        ? (wire as OperationPairRole)
        : 'OTHER';

/**
 * `null` when the line cannot be read as a leg of an entry — an unknown side or
 * amount source, or no id.
 *
 * Unlike a catalog row, an unreadable line here is **fatal to the read** (see
 * {@link mapOperationVersionDetail}). A catalog missing one operation is a
 * catalog missing one operation; a recipe missing one line is a different
 * recipe, and the editor would happily publish it.
 */
export const mapOperationLine = (
    wire: OperationLineWire,
): OperationLine | null => {
    const side = sideOf(wire.side);
    const amountSource = amountSourceOf(wire.amountSource);
    if (!wire.id || !side || !amountSource || !isFiniteInt(wire.pairGroup)) {
        return null;
    }
    return {
        id: wire.id,
        pairGroup: Math.trunc(wire.pairGroup),
        side,
        accountId: wire.accountId ?? null,
        slotLabelKa: wire.slotLabelKa ?? null,
        slotLabelEn: wire.slotLabelEn ?? null,
        accountFilter: wire.accountFilter ?? [],
        amountSource,
        roleLabel: wire.roleLabel ?? null,
        pairRole: pairRoleOf(wire.pairRole),
    };
};

/**
 * `null` when the version cannot be read, **or when any one of its lines
 * cannot**.
 *
 * That is the trial balance's rule rather than the catalog's, and for the trial
 * balance's reason. A recipe with a line silently dropped still renders — as a
 * different recipe, one whose pairs no longer balance — and the editor would
 * then publish that as the version entries post under. Failing the read puts the
 * page in its error state, which is recoverable; publishing a mangled recipe is
 * not.
 */
export const mapOperationVersionDetail = (
    wire: OperationVersionDetailWire | null | undefined,
): OperationVersionDetail | null => {
    const summary = mapOperationVersion(wire);
    if (!summary || !wire || !Array.isArray(wire.lines)) {
        return null;
    }
    const lines: OperationLine[] = [];
    for (const lineWire of wire.lines) {
        const line = mapOperationLine(lineWire);
        if (!line) {
            return null;
        }
        lines.push(line);
    }
    return { ...summary, lines };
};

/**
 * `null` when the body is not a template detail. A `null` `activeVersion` or
 * `draftVersion` is carried through as `null` — both are real states, and the
 * editor renders each differently.
 */
export const mapOperationTemplateDetail = (
    wire: OperationTemplateDetailWire,
): OperationTemplateDetail | null => {
    const status = statusOf(wire.status);
    if (
        !wire.key ||
        !wire.nameKa ||
        !wire.nameEn ||
        !wire.templateId ||
        !status ||
        !Array.isArray(wire.versions)
    ) {
        return null;
    }

    const versions: OperationVersionSummary[] = [];
    for (const versionWire of wire.versions) {
        const version = mapOperationVersion(versionWire);
        if (!version) {
            return null;
        }
        versions.push(version);
    }

    // A version the server sent but this client cannot read is drift, not an
    // absent version — mapping it to `null` would tell the editor there is no
    // draft and quietly offer to create a second one.
    const activeVersion = wire.activeVersion
        ? mapOperationVersionDetail(wire.activeVersion)
        : null;
    if (wire.activeVersion && !activeVersion) {
        return null;
    }
    const draftVersion = wire.draftVersion
        ? mapOperationVersionDetail(wire.draftVersion)
        : null;
    if (wire.draftVersion && !draftVersion) {
        return null;
    }

    return {
        key: wire.key,
        nameKa: wire.nameKa,
        nameEn: wire.nameEn,
        templateId: wire.templateId,
        categoryId: wire.categoryId ?? '',
        status,
        rolesAllowed: rolesOf(wire.rolesAllowed),
        // The server orders these newest-first; that order is carried, not
        // redone.
        versions,
        activeVersion,
        draftVersion,
    };
};

/**
 * A draft line on its way out.
 *
 * The exclusivity the server enforces is applied here rather than trusted from
 * the form: a fixed line sends no slot half and a slot sends no account, so a
 * form that kept the other half's text while the user toggled cannot submit
 * both and collect a `LINE_ACCOUNT_AMBIGUOUS` it did not mean.
 */
export const toLineWire = (line: OperationLineDraft): OperationLineWire => {
    const isSlot = !line.accountId;
    return {
        pairGroup: line.pairGroup,
        side: line.side,
        accountId: isSlot ? null : line.accountId,
        slotLabelKa: isSlot ? line.slotLabelKa : null,
        slotLabelEn: isSlot ? line.slotLabelEn : null,
        accountFilter: isSlot ? line.accountFilter : [],
        pairRole: line.pairRole,
        amountSource: line.amountSource,
        roleLabel: line.roleLabel,
    };
};

export const mapOperationCatalog = (
    wire: OperationCatalogWire,
): OperationCatalog | null => {
    if (!Array.isArray(wire.items)) {
        return null;
    }
    return {
        // The server's order is carried, not redone (BOS-146): most-recorded
        // first over a rolling 90-day window, ties broken by an explicit
        // default order and then by key. Re-sorting here would be a second
        // implementation that has to agree with the first — and only the server
        // has the counts.
        rows: wire.items
            .map(mapOperationRow)
            .filter((row): row is OperationCatalogRow => row !== null),
    };
};

// ── recording ───────────────────────────────────────────────────────────────

export type RecordableSlotWire = {
    lineId?: string;
    pairGroup?: number;
    side?: string;
    labelKa?: string | null;
    labelEn?: string | null;
    accountFilter?: string[] | null;
};

export type RecordableOperationWire = {
    key?: string;
    nameKa?: string;
    nameEn?: string;
    versionId?: string | null;
    version?: number | null;
    pairCount?: number;
    slots?: RecordableSlotWire[] | null;
    unrecordableReason?: string | null;
    recordCount?: number | null;
    reasonDate?: string | null;
    effectiveFrom?: string | null;
    pinnedLines?: RecordablePinnedLineWire[] | null;
    category?: OperationCategoryWire | null;
};

export type RecordablePinnedLineWire = {
    lineId?: string;
    pairGroup?: number;
    side?: string;
    accountId?: string;
    code?: string | null;
    name?: string | null;
};

export type RecordableOperationsWire = {
    date?: string;
    items?: RecordableOperationWire[] | null;
    configuredCount?: number | null;
    earliestEffectiveFrom?: string | null;
    periodStatus?: string | null;
};

export type OperationPostingLineWire = {
    accountId?: string;
    code?: string | null;
    name?: string | null;
    debitTetri?: number;
    creditTetri?: number;
    slotLineId?: string | null;
};

export type OperationPreviewWire = {
    key?: string;
    version?: number;
    date?: string;
    amountTetri?: number;
    vatRateBps?: number;
    lines?: OperationPostingLineWire[] | null;
    totalDebitTetri?: number;
    totalCreditTetri?: number;
};

export type RecordedOperationWire = {
    recordId?: string;
    entryId?: string;
    entryNumber?: number | null;
    date?: string;
    key?: string;
    version?: number;
    source?: string;
    totalDebitTetri?: number;
    lines?: OperationPostingLineWire[] | null;
    replayed?: boolean;
};

const UNRECORDABLE_REASONS: readonly string[] = [
    'NONE',
    'NEEDS_SECOND_AMOUNT',
    'NOT_PUBLISHED',
    'WINDOW_ENDED',
    'WINDOW_NOT_STARTED',
];

const PERIOD_STATUSES: readonly string[] = ['OPEN', 'CLOSED', 'NOT_DEFINED'];

/**
 * An unrecognised or absent period status reads as `OPEN`.
 *
 * Fails **open**, unlike {@link unrecordableReasonOf} next to it, and for the
 * same reason that one fails closed: pick the degrade that cannot invent
 * something. `OPEN` renders no warning at all, and the period gate still refuses
 * the post — so the worst case is a refusal arriving late, which is where it
 * arrived before this field existed. Guessing `CLOSED` would put a red line
 * under a date nothing is wrong with.
 */
const periodStatusOf = (
    wire: string | null | undefined,
): RecordingPeriodStatus =>
    PERIOD_STATUSES.includes(wire ?? '')
        ? (wire as RecordingPeriodStatus)
        : 'OPEN';

/**
 * An unrecognised reason reads as `UNKNOWN`, not as `NONE`.
 *
 * Fails closed on purpose, and it is the one place in this file that does. Every
 * other unknown here degrades to "show less"; this one degrades to "do not offer
 * a form this build does not know how to fill". A newer server that adds a
 * reason is describing an operation this client cannot collect input for, and
 * defaulting to `NONE` would put an enabled Post button in front of a request
 * the server is about to refuse.
 *
 * It degrades to a value of its own rather than borrowing `NEEDS_SECOND_AMOUNT`
 * — which is what it did while that was the only other member — because the row
 * renders its reason as *prose to the user*, and "needs a second amount" would
 * be a specific claim about an operation this build knows nothing about.
 */
const unrecordableReasonOf = (
    wire: string | null | undefined,
): UnrecordableReason =>
    UNRECORDABLE_REASONS.includes(wire ?? '')
        ? (wire as UnrecordableReason)
        : 'UNKNOWN';

/**
 * `null` when the pinned line cannot be read.
 *
 * Dropped rather than fatal, unlike a slot: a slot is a *question* the form must
 * put to the recorder, and one missing renders a form that looks complete and
 * submits an incomplete request. A pinned line is only ever drawn, and the
 * drawing is explicitly the recipe's shape rather than the posting — the server
 * preview replaces it the moment an amount lands, and it is what the Post button
 * gates on either way.
 */
export const mapRecordablePinnedLine = (
    wire: RecordablePinnedLineWire,
): RecordablePinnedLine | null => {
    const side = sideOf(wire.side);
    if (
        !wire.lineId ||
        !side ||
        !wire.accountId ||
        !isFiniteInt(wire.pairGroup)
    ) {
        return null;
    }
    return {
        lineId: wire.lineId,
        pairGroup: Math.trunc(wire.pairGroup),
        side,
        accountId: wire.accountId,
        code: wire.code ?? '',
        name: wire.name ?? '',
    };
};

/**
 * `null` when the slot cannot be read as a question to put to the recorder — no
 * line id, an unknown side, or no label in either locale.
 *
 * A slot that cannot be read is fatal to its operation (see
 * {@link mapRecordableOperation}), not dropped: an operation missing one of its
 * questions renders a form that looks complete and submits an incomplete
 * request, which the server then refuses with `SLOT_MISSING` naming a slot the
 * user was never shown.
 */
export const mapRecordableSlot = (
    wire: RecordableSlotWire,
): RecordableSlot | null => {
    const side = sideOf(wire.side);
    if (!wire.lineId || !side || !isFiniteInt(wire.pairGroup)) {
        return null;
    }
    const labelKa = wire.labelKa ?? '';
    const labelEn = wire.labelEn ?? '';
    if (!labelKa && !labelEn) {
        return null;
    }
    return {
        lineId: wire.lineId,
        pairGroup: Math.trunc(wire.pairGroup),
        side,
        labelKa,
        labelEn,
        accountFilter: wire.accountFilter ?? [],
    };
};

/** `null` when the operation, or any one of its slots, cannot be read. */
export const mapRecordableOperation = (
    wire: RecordableOperationWire,
): RecordableOperation | null => {
    if (!wire.key || !wire.nameKa || !wire.nameEn) {
        return null;
    }
    const slots: RecordableSlot[] = [];
    for (const slotWire of wire.slots ?? []) {
        const slot = mapRecordableSlot(slotWire);
        if (!slot) {
            return null;
        }
        slots.push(slot);
    }

    // A row with no version in force is a real state, not a broken row: it is
    // how the server says "you may record this, but not on this date". So the
    // version is optional here, and it is the *reason* that has to agree with
    // it — an offerable row with nothing to build a form from is the
    // contradiction, and it fails closed rather than reaching the picker
    // selectable.
    const hasVersion = Boolean(wire.versionId) && isFiniteInt(wire.version);
    const reason = unrecordableReasonOf(wire.unrecordableReason);

    return {
        key: wire.key,
        nameKa: wire.nameKa,
        nameEn: wire.nameEn,
        versionId: hasVersion ? (wire.versionId as string) : null,
        version: hasVersion ? Math.trunc(wire.version as number) : null,
        pairCount: isFiniteInt(wire.pairCount) ? Math.trunc(wire.pairCount) : 0,
        slots,
        unrecordableReason:
            reason === 'NONE' && !hasVersion ? 'UNKNOWN' : reason,
        // Absent reads as never-recorded, which is also what the server sends
        // for a brand-new operation. It ranks last either way, and search still
        // reaches it.
        recordCount: isFiniteInt(wire.recordCount)
            ? Math.trunc(wire.recordCount)
            : 0,
        reasonDate: wire.reasonDate || null,
        effectiveFrom: wire.effectiveFrom || null,
        pinnedLines: (wire.pinnedLines ?? [])
            .map(mapRecordablePinnedLine)
            .filter((line): line is RecordablePinnedLine => line !== null),
        category: mapOperationCategory(wire.category),
    };
};

/**
 * `null` only when the body is not a picker list at all — no `date`, or no
 * `items` array.
 *
 * An unreadable **operation** is dropped, the catalog's rule: the picker has no
 * totals to contradict, and one operation short beats a Record tab that will not
 * open. An unreadable **slot** is not dropped — see {@link mapRecordableSlot}.
 */
export const mapRecordableOperations = (
    wire: RecordableOperationsWire,
): RecordableOperations | null => {
    if (!wire.date || !Array.isArray(wire.items)) {
        return null;
    }
    // The server's order is carried, not redone: most-recorded first over a
    // rolling 90-day window, tie-broken by a default order the Templates catalog
    // shares. Re-sorting here would be a second opinion about a ranking the two
    // screens exist to agree on.
    const items = wire.items
        .map(mapRecordableOperation)
        .filter((item): item is RecordableOperation => item !== null);

    // Clamped against what is *offerable*, not against the length of the list.
    // The list carries unavailable rows too, and a draft-only template is one of
    // those while never being "configured" — so `items.length` can legitimately
    // exceed the count and would drag it upwards.
    const recordable = items.filter(
        (item) => item.unrecordableReason === 'NONE',
    ).length;

    return {
        date: wire.date,
        items,
        // An absent count degrades to "nothing is hidden" rather than to zero.
        // The count exists to say how many operations the *date* took away, and
        // a server that does not send it is not claiming any were — whereas a
        // zero would read as "you may record nothing", which is a sentence with
        // its own empty state. Clamped up to what actually arrived for the same
        // reason: `2 configured · 3 available` is not a thing to render.
        configuredCount: Math.max(
            isFiniteInt(wire.configuredCount)
                ? Math.trunc(wire.configuredCount)
                : recordable,
            recordable,
        ),
        earliestEffectiveFrom: wire.earliestEffectiveFrom || null,
        periodStatus: periodStatusOf(wire.periodStatus),
    };
};

/**
 * `null` when the line cannot be read.
 *
 * **Fatal to its response, never dropped** — the trial balance's rule, and the
 * strictest one in this file. These lines are the entry about to be posted, and
 * they are shown under totals the server computed over all of them. Dropping one
 * renders a panel whose lines do not add up to its own total, and the user
 * presses Post on it.
 */
export const mapOperationPostingLine = (
    wire: OperationPostingLineWire,
): OperationPostingLine | null => {
    if (
        !wire.accountId ||
        !isFiniteInt(wire.debitTetri) ||
        !isFiniteInt(wire.creditTetri)
    ) {
        return null;
    }
    return {
        accountId: wire.accountId,
        code: wire.code ?? '',
        name: wire.name ?? '',
        debitTetri: Math.trunc(wire.debitTetri),
        creditTetri: Math.trunc(wire.creditTetri),
        slotLineId: wire.slotLineId ?? null,
    };
};

const mapPostingLines = (
    wire: OperationPostingLineWire[] | null | undefined,
): OperationPostingLine[] | null => {
    if (!Array.isArray(wire)) {
        return null;
    }
    const lines: OperationPostingLine[] = [];
    for (const lineWire of wire) {
        const line = mapOperationPostingLine(lineWire);
        if (!line) {
            return null;
        }
        lines.push(line);
    }
    return lines;
};

/** `null` when the body is not a preview, or any of its lines cannot be read. */
export const mapOperationPreview = (
    wire: OperationPreviewWire,
): OperationPreview | null => {
    const lines = mapPostingLines(wire.lines);
    if (
        !wire.key ||
        !wire.date ||
        !lines ||
        !isFiniteInt(wire.version) ||
        !isFiniteInt(wire.amountTetri) ||
        !isFiniteInt(wire.totalDebitTetri) ||
        !isFiniteInt(wire.totalCreditTetri)
    ) {
        return null;
    }
    return {
        key: wire.key,
        version: Math.trunc(wire.version),
        date: wire.date,
        amountTetri: Math.trunc(wire.amountTetri),
        // A tenant with no rate set in force is a real answer, not a failure:
        // NET equals GROSS and a VAT line computes nil. Absent reads as zero,
        // which is what the impact panel then says in words.
        vatRateBps: isFiniteInt(wire.vatRateBps)
            ? Math.trunc(wire.vatRateBps)
            : 0,
        lines,
        totalDebitTetri: Math.trunc(wire.totalDebitTetri),
        totalCreditTetri: Math.trunc(wire.totalCreditTetri),
    };
};

/**
 * `null` when the body is not a recording result.
 *
 * `entryNumber` is allowed to be null and `replayed` to be absent, and neither
 * is a failure: a number the server did not send is one the confirmation names
 * by other means, and an absent `replayed` is `false`, the common case.
 */
export const mapRecordedOperation = (
    wire: RecordedOperationWire,
): RecordedOperation | null => {
    const lines = mapPostingLines(wire.lines);
    if (
        !wire.recordId ||
        !wire.entryId ||
        !wire.date ||
        !wire.key ||
        !lines ||
        !isFiniteInt(wire.version) ||
        !isFiniteInt(wire.totalDebitTetri)
    ) {
        return null;
    }
    return {
        recordId: wire.recordId,
        entryId: wire.entryId,
        entryNumber: isFiniteInt(wire.entryNumber)
            ? Math.trunc(wire.entryNumber)
            : null,
        date: wire.date,
        key: wire.key,
        version: Math.trunc(wire.version),
        source: wire.source ?? 'OPERATION',
        totalDebitTetri: Math.trunc(wire.totalDebitTetri),
        lines,
        replayed: wire.replayed === true,
    };
};

/**
 * A record request on its way out.
 *
 * `reference` and `note` go as `null` rather than as an empty string. The server
 * normalises blank to null anyway, and an empty `documentRef` is a reference
 * nobody can search for that is not absent either.
 */
export const toRecordWire = (input: RecordOperationInput) => ({
    id: input.id,
    date: input.date,
    amountTetri: input.amountTetri,
    slots: input.slots.map((slot) => ({
        lineId: slot.lineId,
        accountId: slot.accountId,
    })),
    reference: input.reference?.trim() || null,
    note: input.note?.trim() || null,
});

// ── activity ────────────────────────────────────────────────────────────────

export type OperationReversalWire = {
    entryId?: string;
    entryNumber?: number | null;
    entryNumberFormatted?: string | null;
    date?: string;
    reason?: string | null;
    byUserId?: string | null;
    byUserName?: string | null;
    at?: string | null;
};

export type OperationActivityRowWire = {
    recordId?: string;
    key?: string;
    nameKa?: string;
    nameEn?: string;
    templateVersionId?: string;
    version?: number;
    amountTetri?: number;
    recordedByUserId?: string | null;
    recordedByName?: string | null;
    recordedAt?: string;
    entryId?: string;
    entryNumber?: number | null;
    entryNumberFormatted?: string | null;
    date?: string;
    status?: string;
    documentRef?: string | null;
    description?: string | null;
    reversal?: OperationReversalWire | null;
};

export type OperationActivityPageWire = {
    items?: OperationActivityRowWire[] | null;
    page?: number;
    pageSize?: number;
    totalCount?: number;
    totalPages?: number;
};

export type OperationActivityLineWire = {
    accountId?: string;
    code?: string | null;
    name?: string | null;
    debitTetri?: number;
    creditTetri?: number;
};

export type OperationActivitySlotWire = {
    lineId?: string;
    labelKa?: string | null;
    labelEn?: string | null;
    accountId?: string;
    code?: string | null;
    name?: string | null;
};

export type OperationActivityDetailWire = {
    item?: OperationActivityRowWire;
    lines?: OperationActivityLineWire[] | null;
    totalDebitTetri?: number;
    totalCreditTetri?: number;
    slots?: OperationActivitySlotWire[] | null;
};

const ACTIVITY_STATUSES: readonly string[] = ['POSTED', 'REVERSED'];

/**
 * `null` on anything else — including `DRAFT`.
 *
 * A recorded operation posts immediately, so no record can point at a draft. A
 * row claiming otherwise is drift, and rendering it under a Posted/Reversed chip
 * set would have to invent a third state; dropping the row is the smaller lie.
 */
const activityStatusOf = (
    wire: string | undefined,
): OperationActivityRow['status'] | null =>
    typeof wire === 'string' && ACTIVITY_STATUSES.includes(wire)
        ? (wire as OperationActivityRow['status'])
        : null;

/**
 * `null` when the reversal cannot be read.
 *
 * Not fatal to its row: an entry whose `status` is `REVERSED` is reversed
 * whether or not this client can describe the reversal, and the chip is the
 * fact the reader most needs. The drawer says the linkage is unavailable rather
 * than showing a Posted chip over a reversed entry.
 */
export const mapOperationReversal = (
    wire: OperationReversalWire | null | undefined,
): OperationReversal | null => {
    if (!wire?.entryId || !wire.date) {
        return null;
    }
    return {
        entryId: wire.entryId,
        entryNumber: isFiniteInt(wire.entryNumber)
            ? Math.trunc(wire.entryNumber)
            : null,
        entryNumberFormatted: wire.entryNumberFormatted ?? null,
        date: wire.date,
        reason: wire.reason ?? null,
        byUserId: wire.byUserId ?? null,
        byUserName: wire.byUserName ?? null,
        at: wire.at ?? null,
    };
};

/** `null` when the row cannot be read as a recorded operation. */
export const mapOperationActivityRow = (
    wire: OperationActivityRowWire,
): OperationActivityRow | null => {
    const status = activityStatusOf(wire.status);
    if (
        !wire.recordId ||
        !wire.key ||
        !wire.entryId ||
        !wire.date ||
        !wire.recordedAt ||
        !status ||
        !isFiniteInt(wire.amountTetri)
    ) {
        return null;
    }
    return {
        recordId: wire.recordId,
        key: wire.key,
        // A template renamed, blocked or gone still has to render as something a
        // reader recognises; the server falls back to the key and so does this.
        nameKa: wire.nameKa || wire.key,
        nameEn: wire.nameEn || wire.key,
        templateVersionId: wire.templateVersionId ?? '',
        version: isFiniteInt(wire.version) ? Math.trunc(wire.version) : 0,
        amountTetri: Math.trunc(wire.amountTetri),
        recordedByUserId: wire.recordedByUserId ?? null,
        recordedByName: wire.recordedByName ?? null,
        recordedAt: wire.recordedAt,
        entryId: wire.entryId,
        entryNumber: isFiniteInt(wire.entryNumber)
            ? Math.trunc(wire.entryNumber)
            : null,
        entryNumberFormatted: wire.entryNumberFormatted ?? null,
        date: wire.date,
        status,
        documentRef: wire.documentRef ?? null,
        description: wire.description ?? '',
        reversal: mapOperationReversal(wire.reversal),
    };
};

/**
 * `null` only when the body is not a page — no `items` array.
 *
 * An unreadable **row** is dropped, the catalog's rule rather than the trial
 * balance's, and the reason is that this list carries no totals for a missing
 * row to contradict. It does carry a `totalCount`, which is the server's count
 * of what matched rather than of what this client could read — so a dropped row
 * makes the count read one high. That is the right way round: a count that
 * disagrees with a visible list invites a second look, while a silently
 * shortened list does not.
 */
export const mapOperationActivityPage = (
    wire: OperationActivityPageWire,
): OperationActivityPage | null => {
    if (!Array.isArray(wire.items)) {
        return null;
    }
    return {
        rows: wire.items
            .map(mapOperationActivityRow)
            .filter((row): row is OperationActivityRow => row !== null),
        page: isFiniteInt(wire.page) ? Math.trunc(wire.page) : 1,
        pageSize: isFiniteInt(wire.pageSize) ? Math.trunc(wire.pageSize) : 0,
        totalCount: isFiniteInt(wire.totalCount)
            ? Math.trunc(wire.totalCount)
            : 0,
        totalPages: isFiniteInt(wire.totalPages)
            ? Math.trunc(wire.totalPages)
            : 0,
    };
};

/** `null` when the line cannot be read. Fatal to the detail — see below. */
export const mapOperationActivityLine = (
    wire: OperationActivityLineWire,
): OperationActivityLine | null => {
    if (
        !wire.accountId ||
        !isFiniteInt(wire.debitTetri) ||
        !isFiniteInt(wire.creditTetri)
    ) {
        return null;
    }
    return {
        accountId: wire.accountId,
        code: wire.code ?? '',
        name: wire.name ?? '',
        debitTetri: Math.trunc(wire.debitTetri),
        creditTetri: Math.trunc(wire.creditTetri),
    };
};

/**
 * `null` when the body is not a detail, **or when any one of its lines cannot be
 * read**.
 *
 * The trial balance's rule, not the list's, and the difference is what the two
 * surfaces show. The list is a register of what happened; the drawer prints the
 * entry under its own totals, and an entry whose lines do not add up to the
 * total above them is the one thing an audit view must never render. Beside it
 * sits a Reverse button whose dialog mirrors those very lines.
 */
export const mapOperationActivityDetail = (
    wire: OperationActivityDetailWire,
): OperationActivityDetail | null => {
    const row = wire.item ? mapOperationActivityRow(wire.item) : null;
    if (!row || !Array.isArray(wire.lines)) {
        return null;
    }
    const lines: OperationActivityLine[] = [];
    for (const lineWire of wire.lines) {
        const line = mapOperationActivityLine(lineWire);
        if (!line) {
            return null;
        }
        lines.push(line);
    }
    return {
        row,
        lines,
        totalDebitTetri: isFiniteInt(wire.totalDebitTetri)
            ? Math.trunc(wire.totalDebitTetri)
            : 0,
        totalCreditTetri: isFiniteInt(wire.totalCreditTetri)
            ? Math.trunc(wire.totalCreditTetri)
            : 0,
        // A slot that cannot be read is dropped: it is provenance beside the
        // entry, not part of it, and one missing answer does not make the lines
        // above it wrong.
        slots: (wire.slots ?? [])
            .map((slotWire) =>
                slotWire.lineId && slotWire.accountId
                    ? {
                          lineId: slotWire.lineId,
                          labelKa: slotWire.labelKa ?? '',
                          labelEn: slotWire.labelEn ?? '',
                          accountId: slotWire.accountId,
                          code: slotWire.code ?? '',
                          name: slotWire.name ?? '',
                      }
                    : null,
            )
            .filter((slot): slot is OperationActivitySlot => slot !== null),
    };
};

// ── history ─────────────────────────────────────────────────────────────────

export type OperationHistoryVersionWire = {
    id?: string;
    version?: number;
    status?: string;
    effectiveFrom?: string;
    effectiveTo?: string | null;
    createdByUserId?: string | null;
    createdByName?: string | null;
    createdAt?: string;
    pairCount?: number;
    postedEntryCount?: number;
    lines?: OperationLineWire[] | null;
};

export type OperationAuditEventWire = {
    id?: string;
    action?: string;
    version?: number | null;
    actorUserId?: string | null;
    actorName?: string | null;
    at?: string;
};

export type OperationTemplateHistoryWire = {
    key?: string;
    nameKa?: string;
    nameEn?: string;
    status?: string;
    versions?: OperationHistoryVersionWire[] | null;
    events?: OperationAuditEventWire[] | null;
};

const TIMELINE_STATUSES: readonly string[] = [
    'DRAFT',
    'ACTIVE',
    'SUPERSEDED',
    'SCHEDULED',
    'BLOCKED',
];

const timelineStatusOf = (
    wire: string | undefined,
): OperationVersionTimelineStatus | null =>
    typeof wire === 'string' && TIMELINE_STATUSES.includes(wire)
        ? (wire as OperationVersionTimelineStatus)
        : null;

const AUDIT_ACTIONS: readonly string[] = [
    'DRAFT_SAVED',
    'PUBLISHED',
    'BLOCKED',
    'UNBLOCKED',
];

/**
 * `null` when the version, or any one of its lines, cannot be read.
 *
 * The trial balance's rule rather than the catalog's, and for a sharper reason
 * than usual: this line set is one half of a **diff**. A version rendered short
 * one line would produce a diff claiming that line was removed — an audit view
 * asserting a configuration change that never happened, which is worse than
 * showing nothing.
 */
export const mapOperationHistoryVersion = (
    wire: OperationHistoryVersionWire,
): OperationHistoryVersion | null => {
    const status = timelineStatusOf(wire.status);
    if (
        !wire.id ||
        !status ||
        !wire.effectiveFrom ||
        !wire.createdAt ||
        !isFiniteInt(wire.version) ||
        !Array.isArray(wire.lines)
    ) {
        return null;
    }
    const lines: OperationLine[] = [];
    for (const lineWire of wire.lines) {
        const line = mapOperationLine(lineWire);
        if (!line) {
            return null;
        }
        lines.push(line);
    }
    return {
        id: wire.id,
        version: Math.trunc(wire.version),
        status,
        effectiveFrom: wire.effectiveFrom,
        effectiveTo: wire.effectiveTo ?? null,
        createdByUserId: wire.createdByUserId ?? null,
        createdByName: wire.createdByName ?? null,
        createdAt: wire.createdAt,
        pairCount: isFiniteInt(wire.pairCount) ? Math.trunc(wire.pairCount) : 0,
        postedEntryCount: isFiniteInt(wire.postedEntryCount)
            ? Math.trunc(wire.postedEntryCount)
            : 0,
        lines,
    };
};

/** `null` when the event cannot be read as an act on a configuration. */
export const mapOperationAuditEvent = (
    wire: OperationAuditEventWire,
): OperationAuditEvent | null => {
    if (!wire.id || !wire.at || !AUDIT_ACTIONS.includes(wire.action ?? '')) {
        return null;
    }
    return {
        id: wire.id,
        action: wire.action as OperationAuditAction,
        version: isFiniteInt(wire.version) ? Math.trunc(wire.version) : null,
        actorUserId: wire.actorUserId ?? null,
        actorName: wire.actorName ?? null,
        at: wire.at,
    };
};

/**
 * `null` when the body is not a history, **or when any version cannot be read**.
 *
 * An unreadable **event** is dropped instead: the change log is a list of acts
 * with no arithmetic and no diff over it, so one missing line of it is one
 * missing line. A missing *version* changes what the diff beside it claims.
 */
export const mapOperationTemplateHistory = (
    wire: OperationTemplateHistoryWire,
): OperationTemplateHistory | null => {
    const status = statusOf(wire.status);
    if (
        !wire.key ||
        !wire.nameKa ||
        !wire.nameEn ||
        !status ||
        !Array.isArray(wire.versions)
    ) {
        return null;
    }
    const versions: OperationHistoryVersion[] = [];
    for (const versionWire of wire.versions) {
        const version = mapOperationHistoryVersion(versionWire);
        if (!version) {
            return null;
        }
        versions.push(version);
    }
    return {
        key: wire.key,
        nameKa: wire.nameKa,
        nameEn: wire.nameEn,
        status,
        // The server's order is carried, not redone: newest first, on both.
        versions,
        events: (wire.events ?? [])
            .map(mapOperationAuditEvent)
            .filter((event): event is OperationAuditEvent => event !== null),
    };
};
